import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import Alert from 'src/components/alert';
import BarcodeScan from 'src/components/barcode-scan';
import ContainerQRCodes from 'src/components/ContainerQRCodes';
import DimensionalContainerInfo from 'src/components/DimensionalContainerInfo';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import NotFound from 'src/components/not-found';
import Qr from 'src/components/qr';
import ResourceLookupField from 'src/components/resource-lookup-field';
import ScannedContainersList from 'src/components/ScannedContainersList';
import ScanSuccessBanner from 'src/components/ScanSuccessBanner';
import useMaterialLotNamesByContainers from 'src/hooks/useMaterialLotNamesByContainers';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { api } from 'src/utils/api';
import { API_RESOURCES, API_RESOURCES_MAP, BUREAU_BARCODE_FORMAT, LOCALSTORAGE_KEYS } from 'src/utils/constants';
import { pluralWord } from 'src/utils/stringUtils';
import { getRouteURI, getShortUuid, getUuid } from 'src/utils/url';
import userPropType from 'src/utils/user-prop-type';

import routes from '../../../utils/routes';

const ScannedContainerPage = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { uuid: materialContainerUuid } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const printerUri = queryParams.get("printer");
  const dimensional = queryParams.get("dimensional");
  const { scannedContainers } = Object.fromEntries(searchParams.entries()) ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(undefined);
  const [scanningRequested, setScanningRequested] = useState(false);
  const [loadingScanData, setLoadingScanData] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scannedContainersList, setScannedContainersList] = useState([]);
  const [allContainers, setAllContainers] = useState([]);

  const { openActionPanel } = useActionPanelStore();
  const storedScanMode = localStorage.getItem(LOCALSTORAGE_KEYS.SCAN_MODE);
  const isQrScanMode = storedScanMode === JSON.stringify(BUREAU_BARCODE_FORMAT.QR);

  const {
    namesByUuid: materialLotNamesByContainer,
    metaByUuid: containerMetaByUuid,
    isLoading: isMaterialLotsLoading,
  } = useMaterialLotNamesByContainers({
    containerUuids: scannedContainersList,
    containers: allContainers,
  });

  const getInitialData = async () => {
    setIsLoading(true);
    try {
      const container = await api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/${materialContainerUuid}/`).json();

      // If dimensional mode, skip batch validation and just load container data
      if (dimensional === 'true') {
        setAllContainers([container]);
        setIsLoading(false);
        return;
      }

      if (container.current_batch) {
        setError({
          name: 'Container already assigned to a batch',
          text: 'Container already assigned to a batch',
        });
        return;
      }

      // If search params exist apply them to the list of containers.
      const containersFromParams = scannedContainers
        ? scannedContainers.split(',').map(item => item.trim()).filter(Boolean)
        : [];
      const savedContainers = containersFromParams.length > 0 ? containersFromParams : [getUuid(container.uri)];

      // Rebuild state from all saved container UUIDs so refresh keeps lot names.
      const allSavedContainers = await Promise.all(savedContainers.map((uuid) =>
        api.get(`${API_RESOURCES.MATERIAL_CONTAINER}/${uuid}/`).json()
      ));

      setAllContainers(allSavedContainers);
      setScannedContainersList(savedContainers);
    } catch (error) {
      setError({
        name: error.name,
        status: error.response && error.response.status,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => void getInitialData(), [materialContainerUuid]);

  function handleError(error) {
    setScanError(error);
  }

  async function handleScan(url, resourceName, resourceUri) {
    setScanError(null);
    setLoadingScanData(true);

    if (scannedContainersList.includes(getUuid(resourceUri))) {
      handleError(new Error('This container is already scanned.'));
      setLoadingScanData(false);
      return;
    }

    const container = await api.get(resourceUri, { prefixUrl: false }).json();
    if (container.current_batch) {
      handleError(new Error(`This container is already in batch ${getShortUuid(container.current_batch)} 
      and you will need to load it separately. 
      Please scan another container that is not in already in a batch to add it to this group.
`));
      setLoadingScanData(false);
      return;
    }

    const updatedList = [...scannedContainersList, getUuid(resourceUri)];
    setScannedContainersList(updatedList);
    setAllContainers(prevContainers => [...prevContainers, container]);
    // We add container uuids to search params when scanned to preserve state between page refreshes, separated by commas.
    setSearchParams({ scannedContainers: updatedList.join(','), printer: printerUri });

    setLoadingScanData(false);
    setScanningRequested(false);
  }

  const handleShowQRCodes = () => {
    openActionPanel({
      title: 'Print QR Codes',
      content: <ContainerQRCodes
        forDisposableContainers
        containers={allContainers}
        backUri={getRouteURI(routes.materialContainerDetails, { uuid: materialContainerUuid })}
        forResource={allContainers[0]?.uri}
      />,
    });
  };

  if (isLoading) {
    return (
      <>
        <Header excludeFromHistory title="Loading..." user={user} />
        <main role="main" className="text-center">
          <Loader inline text="container" />
        </main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header excludeFromHistory title="Not Found" user={user} />
        <main role="main" className="text-center">
          <NotFound id={materialContainerUuid} text={error.text} />
        </main>
      </>
    );
  }

  // If dimensional mode is active, show informational page instead of batch conversion flow
  if (dimensional === 'true' && allContainers.length > 0) {
    return (
      <DimensionalContainerInfo
        container={allContainers[0]}
        uuid={materialContainerUuid}
        user={user}
        dimensional={dimensional}
      />
    );
  }

  const containerCount = scannedContainersList.length;

  const renderActiveScanner = () => {
    if (isQrScanMode) {
      return (
        <>
          <div className="qr-reader text-white bg-dark rounded-sm overflow-hidden">
            <Qr
              requestedEntity={API_RESOURCES.MATERIAL_CONTAINER}
              hidden={false}
              onScan={handleScan}
              onError={handleError}
            >
              {loadingScanData && (<Loader inline text="data" className="mt-3" />)}
            </Qr>
          </div>
          <Alert name='qr-instructions' variant='info' className='text-center mt-3 mb-0'>
            <span>Use your camera to scan the QR code on the {API_RESOURCES_MAP[API_RESOURCES.MATERIAL_CONTAINER]}.</span>
          </Alert>
          <div className='separator'>OR</div>
          <ResourceLookupField
            resourceName={API_RESOURCES.MATERIAL_CONTAINER}
            successfulScan={scanningRequested}
            scannedContainersList={scannedContainersList}
            onScan={handleScan}
          />
        </>
      );
    }

    return (
      <BarcodeScan
        hideHeader
        containerClasses="p-0"
        successfulScan={false}
        handleScan={handleScan}
        handleError={handleError}
        barcodeError={scanError}
        showInstructions={false}
        instructionText=""
        setBarcodeError={setScanError}
        resourceError={null}
        renderUseWithoutPermanentContainer={() => null}
      >
        {loadingScanData && (<Loader inline text="data" className="mt-3" />)}
      </BarcodeScan>
    );
  };

  return (
    <>
      <Header title="Scan Material QR" user={user} onPrintQRCodesClick={handleShowQRCodes} />
      <main role="main" className="scan-material-page">
        <div className="materials-narrow-column scan-material-page__column">
          <div className="scan-material-page__top">
            {scanningRequested ? (
              renderActiveScanner()
            ) : (
              <ScanSuccessBanner
                isLoading={loadingScanData}
                onScanAnother={() => setScanningRequested(true)}
              />
            )}
            {scanError && (
              <Alert name="qr-error" variant="danger" className="text-center mt-3 mb-0">
                {scanError.message}
              </Alert>
            )}
          </div>

          <section className="scanned-containers-section scanned-containers-section--scrollable" aria-label="Scanned containers">
            <div className="scanned-containers-section__header">
              <h2 className="container-selection-title fs-mobile-20">
                {containerCount} {pluralWord('Container', containerCount)} Scanned
              </h2>
              <p className="container-selection-hint">
                Containers scanned so far:
              </p>
            </div>
            <div className="scanned-containers-section__scroll">
              <ScannedContainersList
                containerUuids={scannedContainersList}
                namesByUuid={materialLotNamesByContainer}
                metaByUuid={containerMetaByUuid}
                isLoading={isMaterialLotsLoading}
                ariaLabel="Containers scanned so far"
              />
            </div>
          </section>

          <div className="scan-material-page__actions">
            <Link
              to={getRouteURI(routes.materialContainerCreateInitialBatch,
                { uuid: materialContainerUuid },
                {
                  scannedContainers: scannedContainersList,
                  printer: printerUri,
                  materialContainerUuid,
                })}
              className="materials-action-link"
            >
              <button
                disabled={scannedContainersList.length === 0}
                type="button"
                className="btn btn-lg btn-primary btn-block fs-mobile-18"
              >
                Use Amount Scanned
              </button>
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};

ScannedContainerPage.propTypes = {
  user: userPropType,
};

ScannedContainerPage.defaultProps = {
  user: null,
};

export default ScannedContainerPage;
